<div
  class="project-group"
  [id]="'project-' + project.projectId"
  [attr.data-testid]="'project-' + project.projectId"
>
  <div class="project-group__banner">
    <div class="project-group__title">
      <span *ngIf="project.rush" class="project-group__rush">
        <span class="material-icons project-group__rush-icon">schedule</span>
        Rush Request
      </span>
      <h2 class="project-group__name">Project ID: {{ project.projectOrderId }}</h2>
      <span class="pill pill--info project-group__category">{{ categoryLabel }}</span>

      <!-- CON-154 — the project-level bulk action cluster, pushed to the right
           of the existing title row (no new layout container). Each button is
           ABSENT when its phase holds fewer than TWO products, DISABLED with a
           tooltip naming what is missing when the phase holds two or more but
           nothing is eligible yet, and enabled with its eligible count
           otherwise. All four are absent under `readOnly`. See
           showBulkActions in the .ts, and the `*ngIf` on the first button
           below for why the threshold is two rather than one.

           Task 9 — `aria-disabled`, not the native `disabled` attribute: a
           `disabled` element accepts neither pointer nor focus events, so the
           matTooltip naming the reason would be unreachable by mouse (no
           hover) AND invisible to a screen reader (nothing can focus it to
           trigger the tooltip's aria-describedby). Left as a real, focusable,
           hoverable button instead — the click handlers below already no-op
           on an empty eligible set, so `aria-disabled` is the only thing
           standing in for the native attribute (see also the
           shared `[aria-disabled="true"]` styling in _components.scss). -->
      <div class="project-group__bulk" *ngIf="showBulkActions">
        <!-- The two-or-more threshold, shared by all four buttons.

             A bulk button only earns its place when it stands in for MORE THAN
             ONE inline control. With a single product in the phase, the
             per-product control sitting directly below in that product's own
             row already does the whole job, and "Submit All Quotes 1" is a
             second button for the same single write — noise, and a second
             place for the vendor to look.

             The axis is PRODUCTS, not physical sites. `inPhase` comes out of
             `bulk-eligibility.util.ts`, whose predicates run over the flat
             `SiteDetail[]` where each row is a site-PRODUCT pair: one physical
             site ordered for three services is three rows sharing one
             `siteNumber` (see list-grouping.util.ts's header). So one site
             carrying three priced products still shows "Submit All Quotes 3" —
             three inline controls collapsed into one — and that is precisely
             the case this feature exists for. Do NOT "simplify" this to
             `project.sites.length` or the grouped `SiteVm` count: that reads
             one site and hides the button exactly where it is most useful. -->
        <button
          type="button"
          class="btn btn--primary"
          *ngIf="bulkSubmit.inPhase.length > 1"
          [attr.aria-disabled]="!!bulkDisabledReason(bulkSubmit.eligible, submitDisabledReason)"
          [matTooltip]="bulkDisabledReason(bulkSubmit.eligible, submitDisabledReason) ?? ''"
          matTooltipPosition="above"
          [matTooltipDisabled]="!bulkDisabledReason(bulkSubmit.eligible, submitDisabledReason)"
          matTooltipTouchGestures="off"
          [attr.data-testid]="bulkTestId('submit-all-quotes')"
          (click)="openSubmitAllQuotes()"
        >
          Submit All Quotes
          <span class="project-group__bulk-count">{{
            bulkSubmit.eligible.length
          }}</span>
        </button>

        <button
          type="button"
          class="btn btn--primary"
          *ngIf="bulkAccept.inPhase.length > 1"
          [attr.aria-disabled]="!!bulkDisabledReason(bulkAccept.eligible, acceptDisabledReason)"
          [matTooltip]="bulkDisabledReason(bulkAccept.eligible, acceptDisabledReason) ?? ''"
          matTooltipPosition="above"
          [matTooltipDisabled]="!bulkDisabledReason(bulkAccept.eligible, acceptDisabledReason)"
          matTooltipTouchGestures="off"
          [attr.data-testid]="bulkTestId('accept-all-offers')"
          (click)="openAcceptAllOffers()"
        >
          Accept All Offers
          <span class="project-group__bulk-count">{{
            bulkAccept.eligible.length
          }}</span>
        </button>

        <!-- Award already settles price and terms, so signAtpAllAwarded's
             `eligible` set is identical to its `inPhase` set: the disabled
             branch below is unreachable by construction and kept only so all
             four buttons stay one shape. The two-or-more threshold is the
             same one documented on Submit All Quotes above — and counts
             awarded PRODUCTS, not awarded physical sites. -->
        <button
          type="button"
          class="btn btn--primary"
          *ngIf="bulkAtp.inPhase.length > 1"
          [attr.aria-disabled]="!!atpBannerDisabledReason"
          [matTooltip]="atpBannerDisabledReason ?? ''"
          matTooltipPosition="above"
          [matTooltipDisabled]="!atpBannerDisabledReason"
          matTooltipTouchGestures="off"
          [attr.data-testid]="bulkTestId('sign-all-atp')"
          (click)="generateATP()"
        >
          Sign All ATPs
          <span class="project-group__bulk-count">{{
            bulkAtp.eligible.length
          }}</span>
        </button>
        <button
          type="button"
          class="btn btn--secondary"
          *ngIf="bulkDecline.inPhase.length > 1"
          [attr.aria-disabled]="!!bulkDisabledReason(bulkDecline.eligible, declineDisabledReason, true)"
          [matTooltip]="bulkDisabledReason(bulkDecline.eligible, declineDisabledReason, true) ?? ''"
          [matTooltipDisabled]="!bulkDisabledReason(bulkDecline.eligible, declineDisabledReason, true)"
          matTooltipPosition="above"
          matTooltipTouchGestures="off"
          [attr.data-testid]="bulkTestId('decline-all')"
          (click)="openDeclineAll()"
        >
          Decline All
          <span class="project-group__bulk-count">{{ bulkDecline.eligible.length }}</span>
        </button>
      </div>
    </div>

    <div class="project-group__meta">
      <span class="project-group__meta-item">Service: {{ serviceScopeLabel }}</span>
      <span class="project-group__meta-item" *ngIf="hasRequestedInfo">
        Requested:
        {{
          requestedVariesBySite
            ? "Varies by site"
            : (requestedDate | date : "MM/dd/yyyy")
        }}
      </span>
      <span class="project-group__meta-item" *ngIf="type === 'active'"
        >{{ siteCount }} {{ siteCount === 1 ? "site" : "sites" }}</span
      >
    </div>

    <div
      class="table-a-banner"
      *ngIf="showTableA && (tableA || tableAItemText)"
      [attr.data-testid]="'project-' + project.projectId + '-table-a'"
    >
      <span class="table-a-banner__label">Table A:</span>
      <span class="table-a-banner__value">{{ tableA }}</span>
      <span
        *ngIf="tableAItemText"
        class="table-a-banner__item-text"
        [attr.data-testid]="
          'project-' + project.projectId + '-table-a-item-text'
        "
      >
        <span class="table-a-banner__item-label"
          >{{ tableAItemLabel || "Item 20/21" }}:</span
        >
        {{ tableAItemText }}
      </span>
    </div>

    <!-- Active variant: ATP-signed stamp + completion status. Completion %/
         progress bar are omitted — the domain has no per-product "complete"
         status to compute X of Y from (see productCount's doc comment in the
         .ts) — so this degrades to a plain in-progress count per the spec's
         "no fabricated numbers" rule. -->
    <ng-container *ngIf="type === 'active'">
      <div
        class="project-group__atp"
        *ngIf="atpStamp as stamp"
        [attr.data-testid]="'project-' + project.projectId + '-atp-stamp'"
      >
        <span class="material-icons project-group__atp-icon">verified</span>
        <span *ngIf="stamp.signedBy"
          >ATP signed by {{ stamp.signedBy }} ·
          {{ stamp.signedDate | date : "MM/dd/yyyy" }}</span
        >
        <span *ngIf="!stamp.signedBy"
          >ATP signed {{ stamp.signedDate | date : "MM/dd/yyyy" }}</span
        >
      </div>

      <div class="project-group__progress">
        <span class="project-group__progress-status"
          >{{ productCount }} {{ productCount === 1 ? "product" : "products" }} in
          progress</span
        >
      </div>
    </ng-container>

    <div
      class="project-group__requirements"
      *ngIf="hasSpecialRequirements"
      [attr.data-testid]="'project-' + project.projectId + '-special-requirements'"
    >
      <span class="material-icons project-group__requirements-icon">assignment</span>
      <div class="project-group__requirements-body">
        <strong>Special Requirements</strong>
        <p>{{ project.specialQuoteRequirements }}</p>
      </div>
    </div>
  </div>

  <div class="project-group__sites" *ngIf="siteBulkSets as bulkBySite">
    <app-site-card
      *ngFor="let site of project.sites; trackBy: trackBySiteKey"
      [site]="site"
      [type]="type"
      [projectId]="project.projectId"
      [rawProject]="project.raw"
      [readOnly]="readOnly"
      [testIdProjectScope]="testIdProjectScope"
      [bulk]="bulkBySite.get(site.key)!"
      [atpInProgress]="atpInProgress"
      (bulkAction)="onSiteBulkAction(site, $event)"
      (atpSigned)="atpSigned.emit($event)"
    ></app-site-card>
  </div>
</div>

<!-- CON-154 — the three bulk ACTION dialogs, hosted here beside the buttons
     that open them. The post-write CONFIRMATION dialogs stay page-hosted via
     ModalService: a bulk write pushes a project-detail refresh that destroys
     this banner, so a confirmation hosted here would never render (the same
     reason `product-line` routes its confirmations through the page).

     `[data]` gets the whole project and `[siteDetails]` only the eligible set.
     That split is load-bearing, not incidental: `projectHasSiteRequirements(data)`
     drives the requirements-acknowledgement gate, whose whole-project scope is
     ratified as UNCHANGED — a special site condition on a site that is NOT part
     of this sweep still has to be acknowledged. Narrowing `[data]` would
     silently narrow that gate. -->
<allbids-dialog
  *ngIf="!readOnly && allBidsActive"
  [active]="allBidsActive"
  [data]="project.raw"
  [siteDetails]="bulkQuoteSites"
  [numberOfBids]="bulkQuoteSites.length"
  (modalClosed)="toggleSubmitAllQuotesModal()"
  [ngClass]="{ 'documents-dialog--active': allBidsActive }"
></allbids-dialog>

<accept-offered-proposal-dialog
  *ngIf="!readOnly && acceptAllOffersActive"
  [active]="acceptAllOffersActive"
  [data]="project.raw"
  [siteDetails]="bulkOfferSites"
  [numberOfAcceptedProposals]="bulkOfferSites.length"
  [scopeLabel]="acceptScopeLabel"
  [multiple]="true"
  (modalClosed)="toggleAcceptAllOffersModal()"
  [ngClass]="{ 'documents-dialog--active': acceptAllOffersActive }"
></accept-offered-proposal-dialog>

<signatp-dialog
  [locked]="rowsLocked(bulkAtpSites)"
  *ngIf="!readOnly && signAtpActive"
  [atpDocumentPath]="atpDocumentPath"
  [active]="signAtpActive"
  [modalHeaderTitle]="childModalHeaderTitle"
  [modalBody]="childModalBody"
  (modalClosed)="toggleATPModal()"
  (signedAtpEvent)="signATP($event)"
  (declineAtpEvent)="declineATP($event)"
  [ngClass]="{ 'documents-dialog--active': signAtpActive }"
></signatp-dialog>

<decline-dialog
  *ngIf="!readOnly && declineAllActive"
  [active]="declineAllActive"
  [multiple]="true"
  [items]="declineAllItems"
  [scopeLabel]="declineScopeLabel"
  (modalClosed)="closeDeclineAllModal()"
  (decline)="declineAll($event)"
></decline-dialog>
